/**
 * SignatureImage — May 2026
 *
 * Single source of truth for rendering a signature ``<img>`` in the
 * clinician-facing app.  Handles every storage shape we currently
 * support without crashing or showing a broken-image icon:
 *
 *   * ``data:image/...;base64,…``  → renders directly.
 *   * ``https://…``                → renders directly (assumed to
 *                                    be a GCS signed URL).
 *   * ``gcs://bucket/path``        → swaps for a fresh signed URL
 *                                    via ``GET /api/users/{user_id}/
 *                                    signature`` (only path that
 *                                    surfaces a current signed URL
 *                                    today).  Falls back to a
 *                                    polite placeholder if the user
 *                                    id is missing or the resolver
 *                                    call fails.
 *   * Empty / null                 → renders nothing (caller's
 *                                    surrounding box stays empty).
 *
 * Replaces the inline ``<img src={selectedNote.provider_signature}>``
 * pattern in App.js that rendered the raw ``gcs://`` scheme directly
 * and triggered the user-reported "I can't see signatures
 * consistently" production bug.
 *
 * Props:
 *   raw            — the raw signature value from the note doc.
 *   userId         — fallback resolver — when the raw value is a
 *                    gcs:// URI (or fails to load), we ask the
 *                    backend for a fresh signed URL by user id.
 *   alt            — alt text.
 *   className      — passed through to the ``<img>``.
 *   resolverCache  — optional outer Map (keyed by ``userId``) so
 *                    repeated renders re-use the same fetched URL
 *                    without thrashing the network.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _isRenderable(s) {
  if (!s || typeof s !== 'string') return false;
  return s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://') || s.startsWith('blob:');
}

export function SignatureImage({
  raw,
  userId,
  alt = 'Signature',
  className = 'h-10 w-auto',
  testId,
}) {
  // ``current`` is the URL we're currently trying to render.  We
  // start with the raw value (if renderable) and swap to the
  // resolved one as needed.
  const [current, setCurrent] = useState(() => (_isRenderable(raw) ? raw : null));
  // ``failed`` short-circuits the render when every fallback path
  // has been exhausted, so we don't infinite-loop on onError.
  const [failed, setFailed] = useState(false);

  // When the raw value is a gcs:// scheme (or anything we can't
  // render directly), resolve it via the API.
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (_isRenderable(raw)) {
      setCurrent(raw);
      return () => { cancelled = true; };
    }
    // Need to resolve via the API.  Requires a userId.
    if (!userId) {
      setCurrent(null);
      return () => { cancelled = true; };
    }
    setCurrent(null);
    axios.get(`${API}/users/${userId}/signature`)
      .then(resp => {
        if (cancelled) return;
        const url = resp?.data?.signature_url;
        if (_isRenderable(url)) {
          setCurrent(url);
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => { cancelled = true; };
  }, [raw, userId]);

  const handleError = () => {
    // If the rendered URL fails (expired GCS signed URL, network
    // hiccup, etc.) try the API resolver as a one-shot fallback.
    if (failed) return;
    if (!userId) {
      setFailed(true);
      return;
    }
    axios.get(`${API}/users/${userId}/signature`)
      .then(resp => {
        const url = resp?.data?.signature_url;
        if (_isRenderable(url) && url !== current) {
          setCurrent(url);
        } else {
          setFailed(true);
        }
      })
      .catch(() => setFailed(true));
  };

  if (!current || failed) {
    return null;
  }

  return (
    <img
      src={current}
      alt={alt}
      className={className}
      style={{ imageRendering: 'crisp-edges' }}
      data-testid={testId}
      onError={handleError}
    />
  );
}

export default SignatureImage;
